// StreakVisual — card unificado da "Ofensiva" (streak) do App CM.
//
// Substitui os 3 lugares onde o app mostrava streak com pouca ênfase:
//   - Home anônima (hero navy)
//   - Dashboard logado (grid card)
//   - /jornada (streak hero)
//
// Recebe apenas dados (não conhece se veio de localStorage ou API).
// Renderiza:
//   - Número grande da ofensiva (48-56px em gold)
//   - Barra semanal de 7 dias (mais antigo → hoje) com chama para dias
//     completos e círculo vazado para pendentes; hoje ganha ring
//   - Mensagem motivacional pastoral dinâmica (sem cara de IA)
//
// Variantes:
//   - "dark"  → sobre fundo navy (Home, Jornada)
//   - "light" → sobre fundo branco/parchment (Dashboard)

import React from "react";
import { Flame } from "lucide-react";

const WEEKDAY_LABELS = ["D", "S", "T", "Q", "Q", "S", "S"];

/**
 * Mensagem pastoral. Sem exclamações excessivas, sem cara de IA, sem
 * clichê motivacional. Fala com o leitor, não com "o usuário".
 */
function buildMessage(streak, longest) {
  if (!streak || streak <= 0) {
    return "Comece hoje. Cada dia com Deus é semente que germina.";
  }
  if (streak === 1) {
    return "Primeiro passo dado. A caminhada começa em um dia.";
  }
  if (streak < 7) {
    const restam = 7 - streak;
    return `Faltam ${restam} ${restam === 1 ? "dia" : "dias"} para completar sua primeira semana firme.`;
  }
  if (streak < 30) {
    return "Uma semana de fidelidade. A raiz está se firmando.";
  }
  if (streak < 90) {
    return "Um mês com Deus. A oração virou hábito, e o hábito virou fruto.";
  }
  if (streak < 365) {
    return "Sua constância tem forma de árvore plantada junto ao ribeiro.";
  }
  return "Um ano de caminhada. Que Deus continue a te sustentar.";
}

function recordLine(longest) {
  if (!longest || longest <= 0) return null;
  const noun = longest === 1 ? "dia" : "dias";
  return `Recorde: ${longest} ${noun} consecutivos`;
}

function DayDot({ day, isToday, variant }) {
  const dark = variant === "dark";
  const label = WEEKDAY_LABELS[new Date(day.date + "T00:00:00").getDay()];
  const activeBg = dark ? "bg-gold text-navy" : "bg-gold text-navy";
  const inactiveBg = dark
    ? "bg-white/8 text-white/40 border border-white/15"
    : "bg-[hsl(var(--parchment))] text-navy/35 border border-navy/10";
  const todayRing = dark ? "ring-2 ring-gold/60" : "ring-2 ring-gold/70";
  const labelColor = dark ? "text-white/45" : "text-navy/45";

  return (
    <div
      className="flex flex-col items-center gap-1.5 min-w-0"
      data-testid={`streak-day-${day.date}`}
      data-active={day.active ? "true" : "false"}
      data-today={isToday ? "true" : "false"}
    >
      <div
        className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-colors ${
          day.active ? activeBg : inactiveBg
        } ${isToday ? todayRing : ""}`}
      >
        {day.active ? (
          <Flame className="w-4 h-4" fill="currentColor" />
        ) : (
          <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60" />
        )}
      </div>
      <span className={`text-[10px] uppercase tracking-widest ${labelColor}`}>
        {label}
      </span>
    </div>
  );
}

/**
 * @param {object} props
 * @param {number} props.current   ofensiva atual (em dias)
 * @param {number} [props.longest] maior ofensiva já alcançada
 * @param {Array<{date:string, active:boolean}>} [props.week] 7 dias ordenados
 * @param {"dark"|"light"} [props.variant]  paleta do fundo onde está
 * @param {string} [props.eyebrow]          rótulo pequeno no topo
 * @param {React.ReactNode} [props.action]  botão/CTA opcional no rodapé
 * @param {string} [props.testid]
 */
export default function StreakVisual({
  current = 0,
  longest = 0,
  week = [],
  variant = "dark",
  eyebrow = "Sua ofensiva",
  action = null,
  testid = "streak-visual",
}) {
  const dark = variant === "dark";
  const message = buildMessage(current, longest);
  const record = recordLine(longest);
  const todayIso = week.length > 0 ? week[week.length - 1].date : null;

  const numberColor = dark ? "text-white" : "text-navy";
  const unitColor = dark ? "text-white/60" : "text-navy/55";
  const eyebrowColor = dark ? "text-[#B8962E]" : "text-[#B8962E]";
  const messageColor = dark ? "text-white/85" : "text-navy/75";
  const recordColor = dark ? "text-white/55" : "text-navy/50";

  return (
    <div data-testid={testid}>
      {/* Header: eyebrow + big number */}
      <div className="flex items-baseline gap-3 flex-wrap">
        <span
          className={`text-[10px] uppercase tracking-[0.3em] ${eyebrowColor}`}
        >
          {eyebrow}
        </span>
      </div>
      <div className="flex items-baseline gap-3 mt-2">
        <span
          className={`font-display leading-none tabular-nums text-[52px] sm:text-[56px] ${numberColor}`}
          data-testid={`${testid}-number`}
        >
          {current}
        </span>
        <span className={`font-sans text-lg ${unitColor}`}>
          {current === 1 ? "dia" : "dias"}
        </span>
      </div>

      {/* Message */}
      <p
        className={`mt-3 text-sm leading-relaxed ${messageColor}`}
        data-testid={`${testid}-message`}
      >
        {message}
      </p>

      {/* Weekly bar (7 dots) */}
      {week && week.length === 7 && (
        <div
          className="mt-5 flex items-center justify-between gap-1"
          data-testid={`${testid}-week`}
        >
          {week.map((day) => (
            <DayDot
              key={day.date}
              day={day}
              isToday={day.date === todayIso}
              variant={variant}
            />
          ))}
        </div>
      )}

      {/* Record line */}
      {record && (
        <p
          className={`mt-4 text-[11px] uppercase tracking-widest ${recordColor}`}
          data-testid={`${testid}-record`}
        >
          {record}
        </p>
      )}

      {/* Optional CTA */}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
